import { motion } from "framer-motion";

import { useTranslation } from "@/core/i18n";

type ResultsHeroProps = {
  beforeImage?: string | null;
  afterImage?: string | null;
  baseScore: number;
  potentialScore: number;
  skinAge: number;
  potentialSkinAge: number;
  skinAgeImprovement: number;
  hasSkinAgeData: boolean;
  showAnalysisScores: boolean;
  firstName?: string | null;
  isWellness: boolean;
  wellnessImageUrl?: string | null;
};

export function ResultsHero({
  beforeImage,
  afterImage,
  baseScore,
  potentialScore,
  skinAge,
  potentialSkinAge,
  skinAgeImprovement,
  hasSkinAgeData,
  showAnalysisScores,
  firstName,
  isWellness,
  wellnessImageUrl,
}: Readonly<ResultsHeroProps>) {
  const { t } = useTranslation();
  const displayName = firstName ? firstName.charAt(0).toUpperCase() + firstName.slice(1) : null;
  const heading = displayName
    ? t("resultsHero.headingWithName", { name: displayName })
    : t("resultsHero.heading");
  const showScores = showAnalysisScores && !isWellness && (baseScore > 0 || potentialScore > 0);
  const heroImage = wellnessImageUrl || afterImage || beforeImage;

  return (
    <motion.div
      className="px-1 pb-3 pt-2 text-center"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <p className="mb-4 text-xs uppercase tracking-wider text-secondary">
        {t("resultsHero.yourResults")}
      </p>

      <div className="relative mb-6 flex items-center justify-center">
        <div
          className="relative shrink-0 overflow-hidden rounded-3xl"
          style={{ width: 200, height: 266 }}
        >
          {heroImage ? (
            <img
              alt={t("resultsHero.resultAlt")}
              className="h-full w-full object-cover"
              src={heroImage}
            />
          ) : (
            <div className="h-full w-full bg-gray-200" />
          )}
        </div>
      </div>

      {showScores && (
        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="mb-4 flex items-center justify-center gap-6"
          initial={{ opacity: 0, y: 10 }}
          transition={{ delay: 0.5 }}
        >
          <ScorePill label={t("resultsHero.now")} value={baseScore} tone="secondary" />
          <ScorePill label={t("resultsHero.potential")} value={potentialScore} tone="primary" />
        </motion.div>
      )}

      {hasSkinAgeData && showScores && (
        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="mb-5 flex items-center justify-center gap-2"
          initial={{ opacity: 0, y: 10 }}
          transition={{ delay: 0.7 }}
        >
          <span className="text-xs text-secondary">{t("resultsHero.skinAge")}</span>
          <span className="text-sm font-bold tabular-nums text-primary">{Math.round(skinAge)}</span>
          <span className="text-xs font-semibold text-emerald-500">
            → {Math.round(potentialSkinAge)}
          </span>
          {skinAgeImprovement > 0 && (
            <span className="rounded bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-bold text-emerald-600">
              {t("resultsHero.yearsImprovement", { years: Math.round(skinAgeImprovement) })}
            </span>
          )}
        </motion.div>
      )}

      <h2 className="text-bold1 text-primary">{heading}</h2>
    </motion.div>
  );
}

type ScorePillProps = {
  readonly label: string;
  readonly value: number;
  readonly tone: "primary" | "secondary";
};

function ScorePill({ label, value, tone }: ScorePillProps) {
  const valueClassName = tone === "primary" ? "text-primary" : "text-secondary";

  return (
    <div className="text-center">
      <span className={`block text-2xl font-bold tabular-nums leading-none ${valueClassName}`}>
        {Math.round(value)}
      </span>
      <span className={`mt-0.5 block text-[10px] uppercase tracking-wider ${valueClassName}`}>
        {label}
      </span>
    </div>
  );
}
